// Dedicated carousel for the per-product "Video Carousel" (metaobject-driven)
// embedded inside the main-product info column. Kept separate from
// see-in-action.js (used by the standalone "See It In Action" section) so
// changes here never affect that other component.
//
// Uses slidesPerView: 'auto' instead of numeric breakpoints because this
// carousel lives inside a narrow flex/grid column (the PDP info column)
// rather than a full-width section. Swiper's numeric breakpoints are keyed
// off the window width, not the container width, so reusing the standalone
// section's breakpoints here caused Swiper to compute slide widths far
// smaller than the fixed-width cards defined in CSS, making the cards
// visually overlap. "auto" makes Swiper measure each slide's real CSS
// width instead, which matches the fixed-width card design.
/* global Swiper */
export function initProductVideoCarousel() {
  const carousels = document.querySelectorAll('.see-in-action[data-carousel-id]');

  carousels.forEach((carousel) => {
    const carouselId = carousel.dataset.carouselId;
    const swiperContainer = document.getElementById(`product-video-carousel-swiper-${carouselId}`);
    const videoItems = carousel.querySelectorAll('.see-in-action__video-item');
    const modal = document.getElementById(`product-video-carousel-modal-${carouselId}`);

    if (!swiperContainer || !modal || videoItems.length === 0) return;

    const modalVideo = modal.querySelector('.see-in-action__modal-video');
    const modalClose = modal.querySelector('.see-in-action__modal-close');
    const modalOverlay = modal.querySelector('.see-in-action__modal-overlay');

    // Initialize Swiper
    if (typeof Swiper !== 'undefined') {
      new Swiper(swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        loop: false,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      });
    }

    // Hover: play silently
    videoItems.forEach((item) => {
      const video = item.querySelector('.see-in-action__video');
      const wrapper = item.querySelector('.see-in-action__video-wrapper');

      if (video && wrapper) {
        video.muted = true;

        const videoUrl = item.dataset.videoUrl;
        if (videoUrl) {
          const source = video.querySelector('source');
          if (source && source.src !== videoUrl) {
            source.src = videoUrl;
          }
        }

        video.load();

        let hoverTimeout;
        let isHovering = false;
        let hasStartedPlaying = false;

        const playVideo = () => {
          const hasSource = video.src || (video.currentSrc && video.currentSrc.length > 0);
          if (!hasSource) {
            return;
          }

          video.muted = true;

          const attemptPlay = () => {
            video.currentTime = 0;
            const playPromise = video.play();

            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  hasStartedPlaying = true;
                })
                .catch(() => {
                  // Silent fail
                });
            }
          };

          if (video.readyState >= 1) {
            attemptPlay();
          } else {
            const playWhenReady = () => {
              attemptPlay();
            };

            video.addEventListener('canplay', playWhenReady, { once: true });
            video.addEventListener('loadeddata', playWhenReady, { once: true });
          }
        };

        wrapper.addEventListener('mouseenter', () => {
          isHovering = true;
          clearTimeout(hoverTimeout);

          if (video.readyState >= 2) {
            playVideo();
          } else {
            hoverTimeout = setTimeout(() => {
              if (isHovering) {
                playVideo();
              }
            }, 100);
          }
        });

        wrapper.addEventListener('mouseleave', () => {
          isHovering = false;
          clearTimeout(hoverTimeout);
          if (hasStartedPlaying) {
            video.pause();
            video.currentTime = 0;
            hasStartedPlaying = false;
          }
        });

        video.addEventListener('canplay', () => {
          if (isHovering && !hasStartedPlaying) {
            playVideo();
          }
        });

        video.addEventListener('loadeddata', () => {
          if (isHovering && !hasStartedPlaying) {
            playVideo();
          }
        });
      }
    });

    // Click: open modal with sound
    videoItems.forEach((item) => {
      const videoUrl = item.dataset.videoUrl;
      const wrapper = item.querySelector('.see-in-action__video-wrapper');
      const playButton = item.querySelector('.see-in-action__play-button');

      function openModal() {
        modalVideo.controls = true;
        modalVideo.muted = false;
        modalVideo.src = videoUrl;
        modal.classList.add('open');
        modalVideo.addEventListener(
          'loadeddata',
          () => {
            modalVideo.play().catch(() => {
              // Silent fail
            });
          },
          { once: true },
        );
        modalVideo.load();
      }

      if (wrapper && videoUrl) {
        wrapper.addEventListener('click', (e) => {
          if (e.target.closest('.see-in-action__play-button')) {
            e.stopPropagation();
          }
          openModal();
        });

        if (playButton) {
          playButton.addEventListener('click', (e) => {
            e.stopPropagation();
            openModal();
          });
        }
      }
    });

    // Close modal
    function closeModal() {
      modal.classList.remove('open');
      modalVideo.pause();
      modalVideo.currentTime = 0;
      modalVideo.src = '';
      modalVideo.controls = true;
    }

    if (modalClose) {
      modalClose.addEventListener('click', closeModal);
    }

    if (modalOverlay) {
      modalOverlay.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('open')) {
        closeModal();
      }
    });
  });
}
